import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { profileIconImage } from "../data/projects";
import ThemeToggle from "./ThemeToggle";

const links = [
  { to: "/", label: "Home", testid: "nav-home" },
  { to: "/portfolio", label: "Portfolio", testid: "nav-portfolio" },
  { to: "/about", label: "About", testid: "nav-about" },
  { to: "/journal", label: "Journal", testid: "nav-journal" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "py-3" : "py-6"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div
          className={`flex items-center justify-between rounded-full px-5 md:px-7 py-3 transition-all duration-500 ${
            scrolled ? "glass-strong" : "glass"
          }`}
        >
          <Link
            to="/"
            data-testid="nav-logo"
            className="flex items-center gap-3 group"
          >
            <span className="w-9 h-9 rounded-full overflow-hidden border border-white shadow-[0_2px_8px_rgba(198,226,255,0.4)] ring-1 ring-[#C6E2FF]/40">
              <img
                src={profileIconImage}
                alt="STXP"
                className="w-full h-full object-cover"
              />
            </span>
            <div className="leading-none">
              <div className="font-fraunces text-lg text-slate-900 dark:text-slate-100">STXP</div>
              <div className="font-manrope text-[10px] tracking-[0.3em] uppercase text-slate-500 dark:text-slate-400 -mt-0.5">
                Photography
              </div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `relative px-4 py-2 rounded-full font-manrope text-sm transition-all duration-300 ${
                    isActive
                      ? "text-slate-900 dark:text-slate-900 bg-white/70 dark:bg-white shadow-sm"
                      : "text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-white/50 dark:hover:bg-white/10"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-2">
            <ThemeToggle testId="nav-theme-toggle" />
            <Link
              to="/contact"
              data-testid="nav-cta"
              className="inline-flex items-center rounded-full bg-slate-900 text-white dark:bg-white dark:text-slate-900 px-5 py-2 text-sm font-manrope font-medium tracking-wide hover:bg-slate-800 dark:hover:bg-slate-100 hover:-translate-y-0.5 transition-all duration-300"
            >
              Get in Touch
            </Link>
          </div>

          <div className="md:hidden flex items-center gap-2">
            <ThemeToggle testId="nav-theme-toggle-mobile" />
            <button
              data-testid="nav-menu-btn"
              aria-label="Toggle menu"
              className="p-2 rounded-full hover:bg-white/60 dark:hover:bg-white/10 transition"
              onClick={() => setOpen((v) => !v)}
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {open && (
          <div
            className="md:hidden mt-3 glass-strong rounded-3xl p-4 flex flex-col gap-1"
            data-testid="nav-mobile-menu"
          >
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`${l.testid}-mobile`}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-2xl font-manrope text-sm ${
                    isActive
                      ? "bg-white/80 dark:bg-white text-slate-900 dark:text-slate-900"
                      : "text-slate-700 dark:text-slate-300 hover:bg-white/60 dark:hover:bg-white/10"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="nav-cta-mobile"
              className="mt-2 px-4 py-3 rounded-2xl bg-slate-900 text-white dark:bg-white dark:text-slate-900 font-manrope text-sm font-medium text-center hover:bg-slate-800 dark:hover:bg-slate-100 transition"
            >
              Get in Touch
            </Link>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
